/**
 * Discounts Page (Settings)
 * List, create, edit, and delete the tenant's reusable discount definitions.
 *
 * A discount is either a PERCENTAGE (value is a whole percent 0-100, e.g. "10"
 * = 10%; unlike tax rates this is NOT a 0-1 fraction) or a FIXED_AMOUNT in
 * dollars. Each posts to a contra-revenue GL account (REVENUE type).
 * Only ADMIN users may change discounts (backend requireRole('ADMIN'));
 * other roles get a read-only table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAPIClient, IDiscountsApi, IGLAccountsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { canManageDiscounts } from '../../utils/permissions';
import {
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  rowActionClass,
  rowDangerActionClass,
} from '../../components/formStyles';
import { Toast } from '../../components/Toast';

/** Backend validateDiscountAccount: postable REVENUE account (contra-revenue has a DEBIT balance, so balance is not checked). */
function isDiscountAccount(account: types.GLAccount): boolean {
  return account.accountType === types.GLAccountType.REVENUE && account.isPostable;
}

const PERCENT_PATTERN = /^\d+(\.\d{1,4})?$/;
const DOLLAR_PATTERN = /^\d+(\.\d{1,2})?$/;

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function formatDiscountValue(discount: types.Discount): string {
  const n = Number(discount.value);
  if (discount.type === types.DiscountType.PERCENTAGE) return `${n}%`;
  return currencyFormatter.format(n);
}

/** Dates are YYYY-MM-DD strings; shown as-is to avoid timezone shifts from new Date(). */
function formatDateRange(discount: types.Discount): string {
  if (!discount.startDate && !discount.endDate) return 'Always';
  if (discount.startDate && discount.endDate) return `${discount.startDate} to ${discount.endDate}`;
  if (discount.startDate) return `From ${discount.startDate}`;
  return `Until ${discount.endDate}`;
}

interface FormState {
  name: string;
  description: string;
  type: types.DiscountType;
  value: string;
  discountAccountId: string;
  active: boolean;
  startDate: string;
  endDate: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  type: types.DiscountType.PERCENTAGE,
  value: '',
  discountAccountId: '',
  active: true,
  startDate: '',
  endDate: '',
};

type Editing = { mode: 'create' } | { mode: 'edit'; discount: types.Discount } | null;

export function DiscountsPage() {
  const { user } = useAuth();
  const apiClient = React.useMemo<IDiscountsApi & IGLAccountsApi>(() => createAPIClient(), []);
  const canManage = canManageDiscounts(user);

  // State
  const [discounts, setDiscounts] = useState<types.Discount[]>([]);
  const [glAccounts, setGLAccounts] = useState<types.GLAccount[]>([]);
  const [glAccountsError, setGLAccountsError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refreshDiscounts = useCallback(async () => {
    const list = await apiClient.listDiscounts();
    setDiscounts(list);
  }, [apiClient]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setGLAccountsError(null);

        // GL accounts only feed the create/edit form, so only admins need them,
        // and a failure there should not hide the discount table.
        const [discountsResult, accountsResult] = await Promise.allSettled([
          apiClient.listDiscounts(),
          canManage ? apiClient.listGLAccounts() : Promise.resolve<types.GLAccount[]>([]),
        ]);

        if (discountsResult.status === 'rejected') {
          throw discountsResult.reason;
        }
        setDiscounts(discountsResult.value);

        if (accountsResult.status === 'fulfilled') {
          setGLAccounts(accountsResult.value);
        } else {
          setGLAccounts([]);
          setGLAccountsError(getErrorMessage(accountsResult.reason, 'Failed to load GL accounts'));
        }
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to load discounts'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [apiClient, canManage]);

  const currentAccountId = editing?.mode === 'edit' ? editing.discount.discountAccount.id : null;
  const discountAccounts = glAccounts
    .filter((a) => isDiscountAccount(a) || a.id === currentAccountId)
    .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm({
      ...EMPTY_FORM,
      // Preselect when there is exactly one valid account, preferring a DEBIT
      // (contra-revenue) account such as "Sales Discounts".
      discountAccountId: (() => {
        const contra = discountAccounts.filter((a) => a.normalBalance === 'DEBIT');
        if (contra.length === 1) return contra[0].id;
        return discountAccounts.length === 1 ? discountAccounts[0].id : '';
      })(),
    });
    setFormError(null);
    setConfirmDeleteId(null);
  };

  const openEdit = (discount: types.Discount) => {
    setEditing({ mode: 'edit', discount });
    setForm({
      name: discount.name,
      description: discount.description ?? '',
      type: discount.type,
      value: String(Number(discount.value)),
      discountAccountId: discount.discountAccount.id,
      active: discount.active,
      startDate: discount.startDate ?? '',
      endDate: discount.endDate ?? '',
    });
    setFormError(null);
    setConfirmDeleteId(null);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;

    const name = form.name.trim();
    if (!name) {
      setFormError('Name is required');
      return;
    }
    if (name.length > 100) {
      setFormError('Name must be 100 characters or less');
      return;
    }

    const valueText = form.value.trim();
    if (form.type === types.DiscountType.PERCENTAGE) {
      if (!PERCENT_PATTERN.test(valueText) || Number(valueText) > 100) {
        setFormError('Enter a percentage between 0 and 100 (e.g. 10 or 12.5)');
        return;
      }
    } else if (!DOLLAR_PATTERN.test(valueText)) {
      setFormError('Enter a dollar amount of 0 or more with at most 2 decimal places (e.g. 25.00)');
      return;
    }

    if (!form.discountAccountId) {
      setFormError('Select a discount GL account');
      return;
    }
    // YYYY-MM-DD strings compare correctly as plain strings.
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      setFormError('End date cannot be before start date');
      return;
    }

    // Send null (not omitted) for blank optional fields so edits can clear them.
    const request: types.CreateDiscountRequest = {
      name,
      description: form.description.trim() || null,
      type: form.type,
      value: valueText,
      discountAccountId: form.discountAccountId,
      active: form.active,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
    };

    try {
      setIsSaving(true);
      setFormError(null);

      if (editing.mode === 'edit') {
        await apiClient.updateDiscount(editing.discount.id, request);
      } else {
        await apiClient.createDiscount(request);
      }

      closeForm();
      await refreshDiscounts();
    } catch (err) {
      setFormError(
        getErrorMessage(err, editing.mode === 'edit' ? 'Failed to update discount' : 'Failed to create discount')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (discount: types.Discount) => {
    try {
      setDeletingId(discount.id);
      setError(null);

      await apiClient.deleteDiscount(discount.id);
      setConfirmDeleteId(null);
      if (editing?.mode === 'edit' && editing.discount.id === discount.id) closeForm();
      await refreshDiscounts();
    } catch (err) {
      setConfirmDeleteId(null);
      setError(getErrorMessage(err, 'Failed to delete discount'));
    } finally {
      setDeletingId(null);
    }
  };

  const isPercentage = form.type === types.DiscountType.PERCENTAGE;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <Link to="/settings" className="text-sm font-semibold text-gray-500 hover:text-blue-600">
            ← Settings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Discounts</h1>
        </div>
        {canManage && !editing && (
          <button onClick={openCreate} className={primaryButtonClass}>
            Add Discount
          </button>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {/* Add / Edit Form */}
      {canManage && editing && (
        <section className="bg-white rounded-lg shadow mb-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                {editing.mode === 'edit' ? `Edit ${editing.discount.name}` : 'New Discount'}
              </h2>

              {formError && <Toast kind="error">{formError}</Toast>}

              {glAccountsError && <Toast kind="error">{glAccountsError}</Toast>}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="discountName" className={labelClass}>
                    Name<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <input
                    id="discountName"
                    type="text"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    disabled={isSaving}
                    maxLength={100}
                    placeholder="Senior Discount"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="discountAccount" className={labelClass}>
                    Discount GL account<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <select
                    id="discountAccount"
                    value={form.discountAccountId}
                    onChange={(e) => setField('discountAccountId', e.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  >
                    <option value="">Select an account</option>
                    {discountAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.accountNumber} · {account.accountName}
                      </option>
                    ))}
                  </select>
                  {!glAccountsError && discountAccounts.length === 0 && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      No postable revenue accounts found. Add a contra-revenue account such as "Sales Discounts"
                      (Revenue, debit balance) in the chart of accounts first.
                    </p>
                  )}
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="discountDescription" className={labelClass}>
                    Description
                  </label>
                  <input
                    id="discountDescription"
                    type="text"
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                    disabled={isSaving}
                    placeholder="Optional"
                    className={inputClass}
                  />
                </div>

                <fieldset className="sm:col-span-3">
                  <legend className={labelClass}>
                    Type<span className="ml-0.5 text-red-600">*</span>
                  </legend>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 py-2.5">
                    {[
                      { value: types.DiscountType.PERCENTAGE, label: 'Percentage (%)' },
                      { value: types.DiscountType.FIXED_AMOUNT, label: 'Fixed amount ($)' },
                    ].map((option) => (
                      <label key={option.value} className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="radio"
                          name="discountType"
                          value={option.value}
                          checked={form.type === option.value}
                          onChange={() => setField('type', option.value)}
                          disabled={isSaving}
                          className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="sm:col-span-1">
                  <label htmlFor="discountValue" className={labelClass}>
                    {isPercentage ? 'Percent' : 'Amount'}<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <div className="relative">
                    {!isPercentage && (
                      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-500">
                        $
                      </span>
                    )}
                    <input
                      id="discountValue"
                      type="text"
                      inputMode="decimal"
                      value={form.value}
                      onChange={(e) => setField('value', e.target.value)}
                      disabled={isSaving}
                      placeholder={isPercentage ? '10' : '25.00'}
                      className={`${inputClass} ${isPercentage ? 'pr-8' : 'pl-7'}`}
                    />
                    {isPercentage && (
                      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                        %
                      </span>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-2 flex items-end pb-2.5">
                  <label htmlFor="discountActive" className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      id="discountActive"
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setField('active', e.target.checked)}
                      disabled={isSaving}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="font-medium">Active</span>
                  </label>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="discountStartDate" className={labelClass}>
                    Start date
                  </label>
                  <input
                    id="discountStartDate"
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setField('startDate', e.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="discountEndDate" className={labelClass}>
                    End date
                  </label>
                  <input
                    id="discountEndDate"
                    type="date"
                    value={form.endDate}
                    min={form.startDate || undefined}
                    onChange={(e) => setField('endDate', e.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
              <button type="button" onClick={closeForm} disabled={isSaving} className={secondaryButtonClass}>
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className={primaryButtonClass}>
                {isSaving ? 'Saving...' : editing.mode === 'edit' ? 'Save Changes' : 'Create Discount'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-4 sm:p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Table */}
        {!isLoading &&
          (discounts.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No discounts yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GL Account</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                    {canManage && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {discounts.map((discount) => {
                    const isConfirming = confirmDeleteId === discount.id;
                    const isDeleting = deletingId === discount.id;
                    return (
                      <tr key={discount.id}>
                        <td data-label="Name" data-primary className="px-4 py-3 break-words min-w-0">
                          <div className="font-semibold text-gray-900">{discount.name}</div>
                          {discount.description && (
                            <div className="text-sm text-gray-500">{discount.description}</div>
                          )}
                        </td>
                        <td data-label="Type" className="px-4 py-3 text-sm text-gray-600">
                          {discount.type === types.DiscountType.PERCENTAGE ? 'Percentage' : 'Fixed amount'}
                        </td>
                        <td data-label="Value" className="px-4 py-3 text-sm text-gray-900">{formatDiscountValue(discount)}</td>
                        <td data-label="GL Account" className="px-4 py-3 text-sm text-gray-600 break-words">
                          {discount.discountAccount.accountNumber} · {discount.discountAccount.accountName}
                        </td>
                        <td data-label="Active" className="px-4 py-3 text-sm">
                          <div className={discount.active ? 'text-green-700 font-medium' : 'text-gray-500'}>
                            {discount.active ? 'Active' : 'Inactive'}
                          </div>
                          <div className="text-xs text-gray-500">{formatDateRange(discount)}</div>
                        </td>
                        {/* Usage counts are always 0 until customer/property/item attachment ships (phase 2). */}
                        <td data-label="Usage" className="px-4 py-3 text-sm text-gray-600">—</td>
                        {canManage && (
                          <td className="px-4 py-3 text-right sm:whitespace-nowrap">
                            {isConfirming ? (
                              <span className="inline-flex flex-wrap items-center gap-3">
                                <span className="text-sm text-gray-700">Delete this discount?</span>
                                <button
                                  onClick={() => handleDelete(discount)}
                                  disabled={isDeleting}
                                  className={rowDangerActionClass}
                                >
                                  {isDeleting ? 'Deleting...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  disabled={isDeleting}
                                  className={rowActionClass}
                                >
                                  Cancel
                                </button>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-4">
                                <button
                                  onClick={() => openEdit(discount)}
                                  disabled={isSaving}
                                  className={rowActionClass}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(discount.id)}
                                  disabled={deletingId !== null}
                                  aria-label={`Delete ${discount.name}`}
                                  className={rowDangerActionClass}
                                >
                                  Delete
                                </button>
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </section>
    </main>
  );
}
